<template>
  <div
    class="fou-add-font-sidebar-block"
  >
    <template v-if="family">
      <div
        v-if="family.styles === 1"
        data-qa-id="single-font-family"
      >
        <template
          v-for="sku in family.skus"
          :key="sku.skuId"
        >
          <div 
            class="is-flex is-flex--justify-between is-flex--align-center" 
            data-qa-id="single-font-items"
            >
            <div class="meta-data is-flex fou-add-font-sidebar-block-title">
              <MFPIcon
                data-qa-id="monotype-icon"
                class-name="fou-add-font-sidebar-monotype-icon"
              />
              <span
                data-qa-id="family-name"
                class="fou-add-font-sidebar-family-title"
              >{{ family.name }}</span>
              <div class="fou-add-font-sidebar-family-seperator" />
              <div class="fou-add-font-sidebar-family-foundry">
                {{ $t('pages.bundle.draft.by') }} <span data-qa-id="vendor-name">{{ family.vendorName }}</span>
              </div>
            </div>
            <div
              v-if="head(sku.styles)?.isFontStyleAdded"
              data-qa-id="tick-icon"
              class="cursor-pointer"
            >
              <button
                data-qa-id="tick-icon-btn"
                @click="updateAddFont({familyId: family.familyId, styleId: head(sku.styles)?.styleId})"
              >
                <TickIcon />
              </button>
            </div>
            <div
              v-else
              data-qa-id="add-btn"
              class="fou-font-add-btn"
              @click="addFontToBundle({family, skuId: sku.skuId, style: head(sku.styles)})"
            >
              <Plus />
            </div>
          </div>
          <FontPreviewer
            :src="getFontRenderSrc({ md5: family?.md5, name: family.name })"
            :family-name="family.name"
          />
        </template>
      </div>
      <div
        v-else
        data-qa-id="multiple-font-family"
      >
        <FouAccordion>
          <FouAccordionItem ref="accordionItemRef">
            <template #heading>
              <div
                data-qa-id="accordion-header"
                class="heading"
              >
                <div class="title">
                  <div class="meta-data is-flex fou-add-font-sidebar-block-title">
                    <MFPIcon
                      data-qa-id="monotype-icon"
                      class-name="fou-add-font-sidebar-monotype-icon"
                    />
                    <span
                      data-qa-id="family-name"
                      class="fou-add-font-sidebar-family-title"
                    >{{ family.name }}</span>
                    <div class="fou-add-font-sidebar-family-seperator" />
                    <div class="fou-add-font-sidebar-family-foundry">
                      {{ $t('pages.bundle.draft.by') }} <span data-qa-id="vendor-name">{{ family.vendorName }}</span>
                    </div>
                  </div>
                </div>

                <FontPreviewer
                  :src="getFontRenderSrc({ md5: family?.md5, name: family.name })"
                  :family-name="family.name"
                />
              </div>
            </template>
            <template #content>
              <div
                v-if="family.isLoading"
                data-qa-id="accordion-font-loader"
                class="wrapper"
              >
                <AnimationProcessing
                  fill="new-blue"
                  class="pageSpinner"
                  data-qa-id="loading-icon"
                />
              </div>
              <div
                v-else
                data-qa-id="font-style-wrapper"
                class="font-styles-list"
              >
                <div
                  v-if="isEmpty(family?.skus)"
                  class="text-center py-1"
                >
                  {{ $t('pages.bundle.draft.no_fonts') }}
                </div>
                <div v-else>
                  <template
                    v-for="sku in family.skus"
                    :key="sku.skuId"
                  >
                    <template v-if="!isEmpty(sku.styles)">
                      <template v-if="isEqual(sku.packageType, SINGLE_STYLE)">
                        <FontStyleItem
                          v-for="style in sku.styles"
                          :key="style.styleId"
                          :style="style"
                          :sku="sku"
                          :family="family"
                          @on-add-font-to-bundle="addFontToBundle"
                          @on-update-add-font="updateAddFont"
                        />
                      </template>
                      <div
                        v-else-if="customPackageCondition(family.skus)"
                        class="text-center py-1"
                      >
                        {{ $t('pages.bundle.draft.no_fonts') }}
                      </div>
                    </template>
                    <div
                      v-else
                      class="text-center py-1"
                    >
                      {{ $t('pages.bundle.draft.no_fonts') }}
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </FouAccordionItem>
        </FouAccordion>
      </div>
    </template>
  </div>
</template>

<script src="./index.js" />
<style lang="scss">
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/variables/fonts';
@import 'mpl-css/lib/assets/global/variables/grid';
@import 'mpl-css/lib/assets/global/variables/layout';
@import 'mpl-css/lib/assets/global/functions/index';
@import 'mpl-css/lib/assets/global/mixins/index';

@import 'src/pages/submissions/stylesheets/variables/accordion.variables';
@import 'src/pages/submissions/stylesheets/accordion';

.fou-add-font-sidebar-block {
  padding: rem(24);
  margin-top: rem(4);
  margin-bottom: rem(4);
  border-bottom: 1px solid #dbdfe5;

  &.active {
    background-color: #f8fbf9;
  }

  .fou-add-font-sidebar-block-title {
    font-size: rem(13);
    align-items: center;

    .fou-add-font-sidebar-monotype-icon {
      margin-right: rem(8);
    }

    .fou-add-font-sidebar-family-title {
      font-family: $mt-medium;
      color: #576579;
    }

    .fou-add-font-sidebar-family-style {
      color: #576579;
      margin-left: rem(16);
    }

    .fou-add-font-sidebar-family-foundry {
      color: #576579;

      .text-underline {
        color: #576579;
      }
    }

    .fou-add-font-sidebar-family-seperator {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 rem(8);

      &::after {
        position: absolute;
        content: '';
        height: rem(2);
        width: rem(2);
        background: #576579;
      }
    }
  }

  .heading {
    padding: 0;

    &:before {
      display: none;
    }

    .title {
      margin-bottom: 1rem;

      &:after {
        right: 0;
        width: rem(12.75);
        height: rem(7.5);
        background-size: cover;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }

  .font-styles-list {
    border: 1px solid #dbdfe5;
    border-radius: rem(16);
    max-height: rem(286);
    overflow-y: auto;
    margin-top: 1rem;
  }
}

.ml-auto {
  margin-left: auto;
}

.text-center {
  text-align: center;
}

.pt-1 {
  padding-top: 1rem;
}

.py-1 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.font-list__na {
  padding: rem(16) rem(24);
}
</style>
